import { Card, Drawer, type DrawerProps } from "@verde/compose-ui";
import type { FundOperationItem } from "@verde/entities";
import {
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  ExternalLinkIcon,
  LessIcon,
  PauseIcon,
  UserIcon,
  WalletIcon,
  XIcon,
} from "@verde/icons";
import { People } from "@verde/modules";
import { Alert, Copyable, IconButton, Menu, Tag } from "@verde/ui-core";
import { dayjs, formatDate, formatToBRL, formatToCPF } from "@verde/utils";
import { useMemo, useReducer } from "react";
import OperationFinishedDialog from "./operation-finished-dialog";

interface OperationDrawerProps extends Pick<DrawerProps, "open" | "onClose"> {
  operation: FundOperationItem;
}

export default function OperationDrawer({
  open,
  onClose,
  operation,
}: OperationDrawerProps) {
  const { icon, tag } = People.VariantByRiskRange({
    situation: operation.client.rating_situation,
  });

  const remainingDays = useMemo(() => {
    const days = dayjs(operation.due_date).diff(dayjs(), "day");

    return days;
  }, [operation.due_date]);

  const ratingSituation = useMemo(
    () => operation.client.rating_situation,
    [operation.client.rating_situation],
  );

  const [isOpenFinishedOperation, toggleFinishedOperation] = useReducer(
    (value) => !value,
    false,
  );

  return (
    <Drawer
      size="sm"
      open={open}
      header={{
        title: (
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center h-6 w-8 p-2 rounded-[24px] border-neutral-light-3 justify-center border ${tag}`}
            >
              {icon || <LessIcon className="h-2 w-2" />}
            </div>
            <p className="text-xl font-bold"> Operação #{operation.id}</p>
          </div>
        ),
        children: (
          <div className="flex gap-2">
            <Tag
              size="sm"
              label={remainingDays >= 0 ? "Monitoramento" : "Concluído"}
            />
            <p className="from-neutral-dark-1 text-base font-medium">
              Vencimento: {formatDate(operation.due_date)} (
              {remainingDays === 0
                ? "hoje"
                : remainingDays < 0
                  ? "Vencido"
                  : `${remainingDays} dia${Math.abs(remainingDays) !== 1 ? "s" : ""}`}
              )
            </p>
          </div>
        ),

        actions: {
          render: (
            <Menu
              size="sm"
              side="left"
              items={[
                {
                  text: "Concluir monitoramento",
                  icon: <PauseIcon />,
                  onClick: () => toggleFinishedOperation(),
                },
              ]}
            >
              <IconButton>
                <EllipsisVerticalIcon />
              </IconButton>
            </Menu>
          ),
        },
      }}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <Card
          size="sm"
          icon={<UserIcon />}
          title="Sobre o cliente"
          classNames={{
            base: "bg-neutral-light-1",
            loader: "bg-neutral-light-pure",
          }}
          action={{
            icon: <ExternalLinkIcon />,
            onClick: () => window.open(`/raio-x-rural/${operation.client.id}`),
          }}
        >
          <Card>
            <div className="grid w-full grid-cols-[1fr_0.5fr] gap-0">
              {[
                {
                  label: "Nome completo",
                  value: operation?.client.name,
                },
                {
                  label: "CPF",
                  value: formatToCPF(operation?.client.document_number),
                },
              ].map(({ label, value }, index) => {
                return (
                  <div
                    key={`profile-info-item-${index + label}`}
                    className={"grid gap-1"}
                  >
                    <small className="text-sm text-neutral-dark-1">
                      {label}
                    </small>
                    <p className="h-4 break-all text-base font-medium text-neutral-dark-3">
                      {typeof value === "string" ? (
                        value ? (
                          <Copyable>{value}</Copyable>
                        ) : (
                          "-"
                        )
                      ) : (
                        value
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
          {["DISQUALIFIED", "WITH_OBSERVATION"].includes(ratingSituation) ?? (
            <Alert
              kind={ratingSituation === "DISQUALIFIED" ? "error" : "warning"}
              icon={
                ratingSituation === "DISQUALIFIED" ? (
                  <XIcon />
                ) : (
                  <ExclamationTriangleIcon />
                )
              }
              bordered
              className={
                ratingSituation === "DISQUALIFIED"
                  ? "bg-off-red"
                  : "bg-off-yellow-bg"
              }
            >
              Riscos encontrados{" "}
            </Alert>
          )}
          {!ratingSituation && (
            <Alert kind="warning" bordered icon={<ExclamationTriangleIcon />}>
              Cliente ainda não qualificado
            </Alert>
          )}
        </Card>

        <Card
          size="sm"
          icon={<WalletIcon />}
          title="Sobre operação"
          classNames={{
            base: "bg-neutral-light-1",
            loader: "bg-neutral-light-pure",
          }}
        >
          <Card>
            <div className="grid w-full grid-cols-[1fr_0.5fr] gap-0">
              {[
                {
                  label: "Valor financiado",
                  value: formatToBRL(operation?.operation_value),
                },
                {
                  label: "Fundo",
                  value: operation?.investment_fund.name,
                },
                {
                  label: "Data vencimento",
                  value: formatDate(operation?.due_date),
                },
                {
                  label: "Situação",
                  value:
                    operation?.status === "MONITORING"
                      ? "A vencer"
                      : "Completo",
                },
                {
                  label: "ID interno",
                  value: operation.operation_id || "--",
                },
              ].map(({ label, value }, index, items) => {
                const isLastLine = index >= items.length - 1;

                return (
                  <div
                    key={`operation-info-item-${index + label}`}
                    className={`grid gap-1 ${
                      (typeof value !== "string" ||
                        (isLastLine && items.length % 2 === 0)) &&
                      "col-span-2"
                    }`}
                  >
                    <small className="text-sm text-neutral-dark-1">
                      {label}
                    </small>
                    <p className="h-4 break-all text-base font-medium text-neutral-dark-3">
                      {typeof value === "string" ? (
                        value ? (
                          <Copyable>{value}</Copyable>
                        ) : (
                          "-"
                        )
                      ) : (
                        value
                      )}
                    </p>
                    {!isLastLine && <hr className="my-4 bg-neutral-light-2" />}
                  </div>
                );
              })}
            </div>
          </Card>
        </Card>
      </div>
      <OperationFinishedDialog
        open={isOpenFinishedOperation}
        onClose={toggleFinishedOperation}
        operationId={operation.id}
      />
    </Drawer>
  );
}
